import React from 'react';
import classNames from 'classnames/bind';

import styles from './Icon.module.css';
const cx = classNames.bind(styles);

// These icons are from the MIT licensed Ionicons set https://ionicons.com/
// and are compressed via https://jakearchibald.github.io/svgomg/ and the
// black color is changed to currentColor
export type IconName =
  | 'add'
  | 'arrowForwardLong' // This icon was modified from arrowForward from Ionicons
  | 'caretDown'
  | 'caretDownCircle'
  | 'caretUp'
  | 'caretUpCircle'
  | 'checkmarkCircle'
  | 'chevronBack'
  | 'chevronDown'
  | 'clock'
  | 'close'
  | 'closeCircle'
  | 'cloudDownload'
  | 'dinosaurZoomLarge' // This icon was taken from the Vault49 designs (not Ionicons)
  | 'dinosaurZoomSmall' // This icon was taken from the Vault49 designs (not Ionicons)
  | 'dynascore'
  | 'dynascoreWithName'
  | 'ear'
  | 'ellipsisHorizontalCircle'
  | 'ellipsisVertical'
  | 'gear'
  | 'invitePerson'
  | 'lockClosed'
  | 'lockOpen'
  | 'marker' // This icon was taken from the Vault49 designs (not Ionicons)
  | 'menu'
  | 'microphone'
  | 'microphoneOff'
  | 'navSelectedItemCurve' // This icon was taken from the Vault49 designs (not Ionicons)
  | 'open'
  | 'pause'
  | 'peopleCircle'
  | 'personCircle'
  | 'play'
  | 'premierePro'
  | 'remove'
  | 'roam'
  | 'search'
  | 'share'
  | 'videoCamera'
  | 'videoCameraOff'
  | 'volumeHigh'
  | 'volumeLow'
  | 'volumeMedium'
  | 'volumeOff';

interface Props {
  name: IconName;
  alignBaseline?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

const Icon = ({ name, alignBaseline, onClick }: Props) => {
  // Most icons are sized to a 512x512 viewBox, but individual icons can
  // override if necessary
  const defaultIconSize = 512;
  let viewBoxWidth = defaultIconSize;
  let viewBoxHeight = defaultIconSize;

  const content: React.SVGProps<SVGElement> | null = (() => {
    switch (name) {
      case 'add':
        return (
          <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="48"
            d="M256 112v288m144-144H112"
          />
        );
      case 'arrowForwardLong':
        viewBoxWidth = 1082;
        return (
          <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="48"
            d="M838 112l144 144-144 144m124-144l-862 .5"
          />
        );
      case 'caretDown':
        return (
          <path
            fill="currentColor"
            d="M98 190.06l139.78 163.12a24 24 0 0036.44 0L414 190.06c13.34-15.57 2.28-39.62-18.22-39.62h-279.6c-20.5 0-31.56 24.05-18.18 39.62z"
          />
        );
      case 'caretDownCircle':
        return (
          <>
            <path
              fill="currentColor"
              d="M342.43 238.23l-74.13 89.09a16 16 0 01-24.6 0l-74.13-89.09A16 16 0 01181.86 212h148.28a16 16 0 0112.29 26.23z"
            />
            <path
              fill="none"
              stroke="currentColor"
              strokeMiterlimit="10"
              strokeWidth="32"
              d="M448 256c0-106-86-192-192-192S64 150 64 256s86 192 192 192 192-86 192-192z"
            />
          </>
        );
      case 'caretUp':
        return (
          <path
            fill="currentColor"
            d="M414 321.94L274.22 158.82a24 24 0 00-36.44 0L98 321.94c-13.34 15.57-2.28 39.62 18.22 39.62h279.6c20.5 0 31.56-24.05 18.18-39.62z"
          />
        );
      case 'caretUpCircle':
        return (
          <>
            <path
              fill="currentColor"
              d="M342.43 273.77l-74.13-89.09a16 16 0 00-24.6 0l-74.13 89.09A16 16 0 00181.86 300h148.28a16 16 0 0012.29-26.23z"
            />
            <path
              fill="none"
              stroke="currentColor"
              strokeMiterlimit="10"
              strokeWidth="32"
              d="M448 256c0-106-86-192-192-192S64 150 64 256s86 192 192 192 192-86 192-192z"
            />
          </>
        );
      case 'checkmarkCircle':
        return (
          <>
            <path
              d="M448 256c0-106-86-192-192-192S64 150 64 256s86 192 192 192 192-86 192-192z"
              fill="none"
              stroke="currentColor"
              strokeMiterlimit="10"
              strokeWidth="32"
            />
            <path
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="32"
              d="M352 176L217.6 336 160 272"
            />
          </>
        );
      case 'chevronBack':
        return (
          <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="48"
            d="M328 112L184 256l144 144"
          />
        );
      case 'chevronDown':
        return (
          <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="48"
            d="M112 184l144 144 144-144"
          />
        );
      case 'clock':
        return (
          <>
            <path
              d="M256 64C150 64 64 150 64 256s86 192 192 192 192-86 192-192S362 64 256 64z"
              fill="none"
              stroke="currentColor"
              strokeMiterlimit="10"
              strokeWidth="32"
            />
            <path
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="32"
              d="M256 128v144h96"
            />
          </>
        );
      case 'close':
        return (
          <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="48"
            d="M368 368L144 144m224 0L144 368"
          />
        );
      case 'closeCircle':
        return (
          <path
            fill="currentColor"
            d="M256 48C141.31 48 48 141.31 48 256s93.31 208 208 208 208-93.31 208-208S370.69 48 256 48zm75.31 260.69a16 16 0 11-22.62 22.62L256 278.63l-52.69 52.68a16 16 0 01-22.62-22.62L233.37 256l-52.68-52.69a16 16 0 0122.62-22.62L256 233.37l52.69-52.68a16 16 0 0122.62 22.62L278.63 256z"
          />
        );
      case 'cloudDownload':
        return (
          <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="32"
            d="M320 336h76c55 0 100-21.21 100-75.6s-53-73.47-96-75.6C391.11 99.74 329 48 256 48c-69 0-113.44 45.79-128 91.2-60 5.7-112 35.88-112 98.4S70 336 136 336h56m0 64.1l64 63.9 64-63.9M256 224v224.03"
          />
        );
      case 'dinosaurZoomLarge':
        return (
          <path
            fill="currentColor"
            d="M193.341 424.081c5.926 0 10.73 4.804 10.73 10.73v27.522a3.032 3.032 0 003.032 3.032h28.455a3.032 3.032 0 003.032-3.032V434.81c0-5.796 4.7-10.495 10.496-10.495a10.496 10.496 0 018.63 10.495v27.523c0 12.882-10.442 23.324-23.324 23.324h-28.456c-12.881 0-23.324-10.442-23.324-23.324V434.81c0-5.925 4.804-10.729 10.73-10.729zm193.253.232l.339.002a10.496 10.496 0 0110.495 10.495v27.523c0 12.882-10.442 23.324-23.324 23.324h-30.321c-12.882 0-23.325-10.442-23.325-23.324V434.81a10.73 10.73 0 0121.226 0v27.523a3.032 3.032 0 003.032 3.032h28.455a3.032 3.032 0 003.032-3.032V434.81a10.496 10.496 0 0110.73-10.495zm16.432-370.854a46.648 46.648 0 0144.783-27.056 81.168 81.168 0 0120.992 3.032 65.541 65.541 0 0134.753 20.292 31.721 31.721 0 018.396 23.325 31.254 31.254 0 01-11.428 21.225 66.24 66.24 0 01-45.016 13.994 11.662 11.662 0 01-11.43-11.895 11.895 11.895 0 0110.264-11.662 43.15 43.15 0 0029.621-8.63 10.496 10.496 0 003.965-5.598 10.962 10.962 0 00-3.265-6.997 42.45 42.45 0 00-23.324-11.662 54.345 54.345 0 00-14.928-2.333 23.324 23.324 0 00-23.324 13.995 176.331 176.331 0 00-12.595 48.747c-1.633 9.563-2.799 19.126-3.965 29.156-3.266 27.522-6.53 125.95-19.36 152.307a64.142 64.142 0 01-52.012 37.785 169.8 169.8 0 01-39.418-1.4h-2.333l-16.093-1.865a118.02 118.02 0 00-52.713 0 136.214 136.214 0 00-72.539 67.64c-19.825 31.488-40.117 63.908-89.798 63.908a149.508 149.508 0 01-39.418-8.163S-5.38 439.009.916 425.947c3.966-8.163 15.628-5.83 23.325-1.4a69.973 69.973 0 0039.184 12.13c36.62 0 51.314-23.325 69.973-52.947a157.439 157.439 0 0184.667-77.436 135.98 135.98 0 0162.742-1.4l16.327 1.4h2.333a142.744 142.744 0 0034.286 1.633 40.584 40.584 0 0033.354-24.957c11.196-21.925 13.994-116.621 17.26-144.144 1.166-9.796 2.332-20.059 3.965-30.322a205.02 205.02 0 0114.694-55.045z"
          />
        );
      case 'dinosaurZoomSmall':
        return (
          <path
            fill="currentColor"
            d="M385.216 369.158a14.862 14.862 0 0115.977 15.605v27.124c0 15.596-12.643 28.239-28.238 28.239h-28.982a28.239 28.239 0 01-28.61-28.239v-27.124a15.234 15.234 0 0130.096 0v25.266h24.523V384.02a14.862 14.862 0 0115.234-14.862zm-134.877 0a15.234 15.234 0 0115.234 15.605v26.381c-.203 15.656-12.953 28.24-28.61 28.239h-27.867c-15.596 0-28.239-12.643-28.239-28.239V384.02a14.862 14.862 0 0114.863-14.862 15.234 15.234 0 0115.234 15.605v25.266h24.151V384.02c.202-8.268 6.964-14.865 15.234-14.862zM396.735 100.89a50.904 50.904 0 0147.931-28.982 93.634 93.634 0 0121.55 2.973 68.739 68.739 0 0136.414 20.064 37.156 37.156 0 019.289 27.124 34.184 34.184 0 01-13.377 24.152 67.996 67.996 0 01-46.816 15.234l-.41-.02a16.72 16.72 0 01-11.109-5.182 15.606 15.606 0 01-4.458-11.147 15.977 15.977 0 0116.348-15.977 39.386 39.386 0 0026.01-7.431l2.229-2.23-2.23-3.344a40.129 40.129 0 00-20.064-10.032c-17.835-4.83-27.124-1.486-32.326 8.918a171.661 171.661 0 00-11.89 46.073c-1.857 9.29-3.715 18.578-3.715 28.239a245.602 245.602 0 01-20.065 82.487 66.51 66.51 0 01-54.247 39.757c-13.227.257-26.44-.99-39.386-3.716h-18.207a111.468 111.468 0 00-49.417 0 127.817 127.817 0 00-68.368 64.28c-16.237 37.334-52.567 61.94-93.261 63.166a93.262 93.262 0 01-40.872-8.546h-2.601s-30.468-21.922-22.294-34.184c8.174-12.261 23.037-8.917 31.954 1.486a43.844 43.844 0 0035.299 12.262c33.069 0 46.445-22.294 65.395-49.046A157.17 157.17 0 01219.5 279.61a134.877 134.877 0 0164.28 0h16.349a138.964 138.964 0 0032.325 0 34.555 34.555 0 0028.982-21.922 220.708 220.708 0 0016.72-71.34c1.115-10.032 3.716-20.064 3.716-30.096a201.015 201.015 0 0114.863-55.363z"
          />
        );
      case 'dynascore':
        return (
          <path
            fill="currentColor"
            d="M220.034 377.445a9.437 9.437 0 00-9.363 9.363v24.15a2.6 2.6 0 01-2.601 2.75H183.1a2.675 2.675 0 01-2.675-2.6v-24.3a9.363 9.363 0 10-18.726 0v24.15c.04 11.791 9.61 21.328 21.401 21.328h24.969c11.761-.04 21.286-9.565 21.327-21.327v-24.151a9.363 9.363 0 00-9.363-9.363zM340.937 377.445a9.437 9.437 0 00-9.363 9.363v24.15a2.6 2.6 0 01-2.601 2.602H303.93a2.6 2.6 0 01-2.6-2.601v-24.151a9.363 9.363 0 00-18.727 0v24.15c.041 11.763 9.566 21.287 21.327 21.328h25.043c11.762-.04 21.286-9.565 21.327-21.327v-24.151a9.363 9.363 0 00-9.363-9.363z M453.89 0H58.11C26.035.042.04 26.55 0 59.263v393.474C.04 485.45 26.034 511.958 58.11 512h395.78c32.093 0 58.11-26.533 58.11-59.263V59.263C512 26.533 485.983 0 453.89 0zm35.891 452.737c-.04 20.186-16.098 36.528-35.892 36.528H58.111c-19.782 0-35.818-16.355-35.818-36.528V384.53a74.046 74.046 0 0034.555 7.579c43.62 0 61.529-29.101 78.843-57.293 14.862-24.175 30.319-49.26 63.61-60.628 14.342-4.85 29.724-2.652 46.37 0 4.681.682 9.512 1.364 14.119 1.82h2.006a138.478 138.478 0 0034.703 1.363c20.039-2.247 37.35-15.299 45.33-34.179 11.37-23.569 14.267-49.032 17.091-73.738 1.04-9.094 2.08-17.658 3.418-26.221 1.75-15 5.5-29.687 11.147-43.652 3.704-8.174 12.005-13.133 20.807-12.429 4.477.105 8.922.792 13.227 2.046 7.58 1.357 14.577 5.03 20.064 10.534a9.48 9.48 0 012.75 6.139 9.403 9.403 0 01-3.419 5.077c-7.447 5.73-16.717 8.43-26.008 7.579-5.664 0-10.255 4.682-10.255 10.458s4.591 10.458 10.255 10.458c14.192.942 28.224-3.513 39.384-12.504 5.992-4.67 9.878-11.613 10.775-19.25.553-7.913-2.138-15.707-7.43-21.522-8.205-8.92-18.98-14.945-30.765-17.203a71.727 71.727 0 00-18.43-2.728c-16.676-.725-32.148 8.838-39.235 24.25a181.973 181.973 0 00-12.93 49.336c-1.412 9.019-2.527 18.264-3.493 27.207-2.824 23.948-5.425 46.607-14.862 66.918-5.006 12.38-16.288 20.943-29.353 22.28-9.923.746-19.898.313-29.724-1.288h-2.155c-4.459-.455-8.917-1.061-13.599-1.743-18.726-3.107-37.378-5.532-55.881.91-40.277 13.565-58.483 43.197-74.311 69.266-15.828 26.07-29.724 47.517-62.05 47.517-12.69.976-25.205-3.471-34.554-12.277V59.263c0-20.173 16.036-36.528 35.818-36.528h395.778c19.794 0 35.851 16.342 35.892 36.528v393.474z"
          />
        );
      case 'dynascoreWithName':
        return (
          <path
            fill="currentColor"
            d="M453.775 0C485.932 0 512 26.587 512 59.383v393.234C512 485.413 485.932 512 453.775 512H58.225C26.068 512 0 485.413 0 452.617V59.383C0 26.587 26.068 0 58.225 0zm0 22.684H58.225c-9.548-.015-18.71 3.846-25.461 10.732-6.752 6.885-10.538 16.229-10.522 25.967v299.171c7.307 7.601 18.253 12.233 34.556 12.233 32.402 0 45.212-20.962 61.515-47.506 16.04-26.099 34.207-55.642 74.44-69.27 16.975-5.727 33.763-4.19 50.07-1.97l1.601.22c1.334.186 2.665.374 3.992.562 4.862.653 9.142 1.277 13.596 1.752l2.212.089A122.231 122.231 0 00294.181 256c13.246-1.485 22.853-8.759 29.345-22.269 9.782-20.309 12.402-42.963 15.139-66.924 1.048-8.937 2.096-18.26 3.551-27.168 2.591-15.915 5.444-33.76 12.897-49.377 7.453-15.618 21.398-24.198 39.244-24.198a72.63 72.63 0 0118.399 2.672c14.061 3.74 24.047 9.442 30.568 17.37 5.266 5.832 7.95 13.615 7.423 21.526-.88 7.63-4.77 14.566-10.771 19.21-10.51 8.818-23.115 12.767-39.36 12.53-5.204-.653-9.085-5.206-8.995-10.552.09-5.347 4.122-9.762 9.344-10.232 11.441.178 19.36-2.197 25.998-7.75a8.734 8.734 0 003.406-5.047 9.646 9.646 0 00-2.737-6.146c-3.639-4.454-10.393-7.987-20.058-10.57a52.336 52.336 0 00-13.246-1.93c-10.015 0-16.827 4.067-20.816 12.44-6.084 12.708-8.763 29.157-11.12 43.647-1.369 8.373-2.359 17.102-3.407 26.188-2.824 24.673-5.764 50.178-17.118 73.694-9.549 19.863-25.27 31.562-45.474 33.848-11.56.973-23.192.515-34.643-1.366l-2.01-.237c-4.657-.505-9.46-1.188-14.148-1.841-16.448-2.346-31.994-4.543-46.376.327-28.95 9.738-44.347 30.017-57.677 51.079l-.916 1.454c-.305.484-.608.97-.91 1.455l-.906 1.457-.451.728-.9 1.457c-.599.97-1.196 1.941-1.792 2.91l-1.039 1.69c-16.98 27.611-35.031 55.585-77.827 55.585a74.247 74.247 0 01-34.556-7.482v68.439c.015 9.715 3.816 19.025 10.565 25.88s15.893 10.694 25.418 10.67h395.55c19.786-.016 35.822-16.37 35.838-36.55V59.383c.023-9.715-3.74-19.04-10.463-25.924-6.721-6.883-15.85-10.759-25.375-10.775zM287.058 378.17a9.374 9.374 0 0114.197 8.552v24.105a2.62 2.62 0 002.62 2.65h24.979a2.65 2.65 0 002.649-2.65v-24.105a9.374 9.374 0 019.345-9.374 9.374 9.374 0 019.49 9.374v24.105c-.015 11.79-9.578 21.34-21.368 21.34h-25.095c-11.785 0-21.339-9.555-21.339-21.34v-24.105a9.374 9.374 0 014.522-8.552zm-120.874 0a9.374 9.374 0 0114.197 8.552v24.105a2.65 2.65 0 002.62 2.65h25.007a2.62 2.62 0 002.62-2.65v-24.105a9.374 9.374 0 019.375-9.374 9.374 9.374 0 019.345 9.374v24.105c0 11.785-9.554 21.34-21.34 21.34h-25.007c-11.786 0-21.34-9.555-21.34-21.34v-24.105a9.374 9.374 0 014.523-8.552zm-41.758-229.464a32.606 32.606 0 0118.05 5.124l-5.822 11.266a21.776 21.776 0 00-11.907-3.58l.174.087c-8.039 0-14.556 6.517-14.556 14.556 0 8.04 6.517 14.556 14.556 14.556a21.776 21.776 0 0011.907-3.58l5.823 11.266a32.839 32.839 0 01-18.225 5.124c-16.594 0-28.413-10.946-28.413-27.424s11.82-27.395 28.413-27.395zm148.648 0c15.605 0 24.95 9.928 24.862 27.511v2.679h-38.428a13.45 13.45 0 0014.556 12.489 21.893 21.893 0 0015.4-7.016l6.9 9.898a31.441 31.441 0 01-22.3 9.258c-16.798 0-27.715-10.946-27.715-27.308 0-16.36 10.568-27.51 26.725-27.51zm-203.03-.873a54.15 54.15 0 0118.283 3.552l-4.512 11.324a38.4 38.4 0 00-14.557-3.551c-3.988 0-5.21 1.106-5.21 3.115 0 2.008 1.338 3.115 4.89 4.687l9.258 3.988c7.22 3.028 11.354 7.366 11.412 15.255 0 11.354-8.472 16.478-21.281 16.478a46.586 46.586 0 01-22.067-5.444l5.59-11.645a34.731 34.731 0 0017.263 5.356c4.221 0 6.346-1.135 6.346-3.697 0-2.009-1.543-2.999-4.454-4.454l-9.258-4.018c-8.18-3.697-11.645-8.675-11.645-15.342 0-10.044 7.017-15.604 19.942-15.604zm104.776-.146c16.39 0 28.53 11.238 28.53 27.395s-12.169 27.395-28.53 27.395-28.501-11.238-28.501-27.395 12.14-27.395 28.501-27.395zm50.073 13.625a16.885 16.885 0 0117.031-12.49h.553v14.702h-1.543c-10.917 0-16.157 4.688-16.157 17.264v21.048H211.18v-51.441h13.48v10.917zm-44.718.219a14.556 14.556 0 10-5.355 28.02 13.77 13.77 0 0014.382-14.556 14.556 14.556 0 00-9.027-13.464zm100.377.84a10.8 10.8 0 00-8.147-2.631 11.441 11.441 0 00-12.46 10.364h24.28a10.8 10.8 0 00-3.673-7.733zm30.601-11.976v2.503h-2.561v7.657h-2.737v-7.657h-2.562v-2.503h7.86zm5.881 0l1.922 4.425 1.921-4.425h2.038l2.853 10.16h-2.795l-1.426-5.066h-.03l-1.659 3.698h-1.805l-1.66-3.698h-.028l-1.427 5.066h-2.795l2.853-10.16h2.038zM124.98 74.178l13.042 31.18 13.363-31.15h14.265l-29.113 65.386a45.066 45.066 0 00-12.023-1.601h-1.252l7.948-17.235-22.271-46.58h16.04zm129.288-1.572a18.72 18.72 0 0117.147 8.909h.233v-7.337h13.654v51.471h-13.654V118.4h-.233a18.72 18.72 0 01-17.147 8.909c-13.48 0-24.746-9.695-24.746-27.308s11.267-27.395 24.746-27.395zM102.767 47.657v77.992H89.2V118.4h-.232a18.72 18.72 0 01-17.148 8.909c-13.595 0-24.745-9.695-24.745-27.308S58.34 72.606 71.82 72.606a18.72 18.72 0 0117.148 8.909h.232V47.657h13.567zm99.507 24.854l.378.008c11.15 0 19.388 6.23 19.388 21.369v31.761h-13.712V97.12c0-8.355-3.551-11.907-10.567-11.907s-12.053 4.105-12.053 15.255v25.182h-13.566V74.18h13.479v7.802h.204a18.661 18.661 0 0116.827-9.462zm55.458 12.934A13.479 13.479 0 00243.787 100a13.945 13.945 0 1027.86 0 13.479 13.479 0 00-13.915-14.556zm-182.105-.008l-.371.008A13.479 13.479 0 0061.34 100a13.479 13.479 0 0013.916 14.556A13.508 13.508 0 0089.2 100.001a13.508 13.508 0 00-13.944-14.556z"
          />
        );
      case 'ear':
        return (
          <path
            fill="currentColor"
            d="M339.583 433.333C332.938 433.333 326.75 431.958 322.167 429.895 305.895 421.418 294.438 409.73 282.98 375.355 271.292 339.605 249.292 322.875 228.208 306.605 210.104 292.625 191.313 278.188 175.042 248.624 162.896 226.625 156.25 202.562 156.25 181.249 156.25 117.083 206.667 66.666 270.833 66.666 335 66.666 385.418 117.083 385.418 181.249H431.25C431.25 91.187 360.895 20.833 270.833 20.833 180.771 20.833 110.417 91.187 110.417 181.249 110.417 210.125 119.125 241.979 134.938 270.625 155.792 308.438 180.313 327.458 200.25 342.813 218.813 357.02 232.104 367.333 239.438 389.792 253.188 431.5 270.833 454.875 302 471.145 313.688 476.417 326.52 479.167 339.583 479.167 390.23 479.167 431.25 438.145 431.25 387.5H385.418C385.418 412.708 364.792 433.333 339.583 433.333ZM125.083 35.5 92.542 2.958C46.938 48.562 18.75 111.583 18.75 181.249 18.75 250.915 46.938 313.938 92.542 359.543L124.854 327.228C87.729 289.875 64.583 238.312 64.583 181.249 64.583 124.187 87.729 72.625 125.083 35.5ZM213.542 181.249C213.542 212.874 239.208 238.541 270.833 238.541 302.458 238.541 328.125 212.874 328.125 181.249 328.125 149.625 302.458 123.958 270.833 123.958 239.208 123.958 213.542 149.625 213.542 181.249Z"
          />
        );
      case 'ellipsisHorizontalCircle':
        return (
          <>
            <circle fill="currentColor" cx="256" cy="256" r="26" />
            <circle fill="currentColor" cx="346" cy="256" r="26" />
            <circle fill="currentColor" cx="166" cy="256" r="26" />
            <path
              fill="none"
              stroke="currentColor"
              strokeMiterlimit="10"
              strokeWidth="32"
              d="M448 256c0-106-86-192-192-192S64 150 64 256s86 192 192 192 192-86 192-192z"
            />
          </>
        );
      case 'ellipsisVertical':
        return (
          <>
            <circle fill="currentColor" cx="256" cy="96" r="48" />
            <circle fill="currentColor" cx="256" cy="256" r="48" />
            <circle fill="currentColor" cx="256" cy="416" r="48" />
          </>
        );
      case 'gear':
        return (
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            fill="currentColor"
            d="M416.048 256C416.048 263.253 415.408 270.08 414.554 276.907L459.568 312.107C463.619 315.307 464.688 321.066 462.128 325.76L419.459 399.574C417.539 402.986 413.914 404.906 410.288 404.906 409.008 404.906 407.728 404.694 406.448 404.266L353.328 382.934C342.234 391.254 330.288 398.506 317.274 403.84L309.167 460.374C308.527 465.494 304.047 469.334 298.714 469.334H213.381C208.047 469.334 203.567 465.494 202.927 460.374L194.821 403.84C181.807 398.506 169.861 391.466 158.767 382.934L105.647 404.266C104.581 404.694 103.301 404.906 102.021 404.906 98.181 404.906 94.554 402.986 92.634 399.574L49.967 325.76C47.407 321.066 48.474 315.307 52.527 312.107L97.541 276.907C96.687 270.08 96.047 263.04 96.047 256 96.047 248.96 96.687 241.92 97.541 235.093L52.527 199.893C48.474 196.693 47.194 190.933 49.967 186.24L92.634 112.427C94.554 109.013 98.181 107.093 101.807 107.093 103.087 107.093 104.367 107.307 105.647 107.733L158.767 129.067C169.861 120.747 181.807 113.493 194.821 108.16L202.927 51.627C203.567 46.507 208.047 42.667 213.381 42.667H298.714C304.047 42.667 308.527 46.507 309.167 51.627L317.274 108.16C330.288 113.493 342.234 120.533 353.328 129.067L406.448 107.733C407.514 107.307 408.794 107.093 410.074 107.093 413.914 107.093 417.539 109.013 419.459 112.427L462.128 186.24C464.688 190.933 463.619 196.693 459.568 199.893L414.554 235.093C415.408 241.92 416.048 248.747 416.048 256ZM373.379 256C373.379 251.52 373.168 247.04 372.314 240.427L369.328 216.32 388.314 201.387 411.139 183.253 396.208 157.44 369.114 168.32 346.499 177.493 327.088 162.56C318.554 156.16 310.021 151.253 300.847 147.413L278.234 138.24 274.821 114.133 270.767 85.333H241.114L236.847 114.133 233.434 138.24 210.821 147.413C202.074 151.04 193.327 156.16 184.154 162.987L164.954 177.493 142.767 168.533 115.674 157.653 100.741 183.467 123.781 201.387 142.767 216.32 139.781 240.427C139.141 246.827 138.714 251.733 138.714 256 138.714 260.267 139.141 265.173 139.781 271.787L142.767 295.893 123.781 310.827 100.741 328.746 115.674 354.56 142.767 343.68 165.381 334.506 184.794 349.44C193.327 355.84 201.861 360.746 211.034 364.586L233.647 373.76 237.061 397.866 241.114 426.666H270.981L275.247 397.866 278.661 373.76 301.274 364.586C310.021 360.96 318.767 355.84 327.939 349.014L347.139 334.506 369.328 343.466 396.419 354.346 411.354 328.534 388.314 310.613 369.328 295.68 372.314 271.573C372.954 265.173 373.379 260.48 373.379 256ZM256.047 170.667C208.901 170.667 170.714 208.853 170.714 256 170.714 303.147 208.901 341.334 256.047 341.334 303.194 341.334 341.379 303.147 341.379 256 341.379 208.853 303.194 170.667 256.047 170.667ZM213.381 256C213.381 279.467 232.581 298.667 256.047 298.667 279.514 298.667 298.714 279.467 298.714 256 298.714 232.533 279.514 213.333 256.047 213.333 232.581 213.333 213.381 232.533 213.381 256Z"
          />
        );
      case 'invitePerson':
        return (
          <path
            fill="currentColor"
            d="M324.182 256C374.41 256 415.09 215.725 415.09 166 415.09 116.275 374.41 76 324.182 76 273.954 76 233.272 116.275 233.272 166 233.272 215.725 273.954 256 324.182 256ZM324.182 121C349.182 121 369.636 141.25 369.636 166 369.636 190.75 349.182 211 324.182 211 299.182 211 278.728 190.75 278.728 166 278.728 141.25 299.182 121 324.182 121ZM324.182 301C263.5 301 142.364 331.15 142.364 391V436H506V391C506 331.15 384.864 301 324.182 301ZM187.818 391C192.818 374.8 263.046 346 324.182 346 385.546 346 456 375.026 460.546 391H187.818ZM119.636 323.5V256H187.818V211H119.636V143.5H74.182V211H6V256H74.182V323.5H119.636Z"
          />
        );
      case 'lockClosed':
        return (
          <>
            <path
              d="M336 208v-95a80 80 0 00-160 0v95"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="32"
            />
            <rect
              x="96"
              y="208"
              width="320"
              height="272"
              rx="48"
              ry="48"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="32"
            />
          </>
        );
      case 'lockOpen':
        return (
          <>
            <path
              d="M336 112a80 80 0 00-160 0v96"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="32"
            />
            <rect
              x="96"
              y="208"
              width="320"
              height="272"
              rx="48"
              ry="48"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="32"
            />
          </>
        );
      case 'marker':
        return (
          <path
            fill="currentColor"
            d="M96 34.76v336.055l2.066.923c12.256 4.979 29.988 8.406 50.061 9.283l10.218.222h195.31c19.594 0 37.965-2.582 51.967-6.83l7.851-2.74L416 370.54V34.76h96v349.793c0 60.499-67.523 90.168-147.805 92.536l-10.54.154h-195.31C76.1 477.243 6.067 450.183.373 392.28L0 384.553V34.76h96z"
          />
        );
      case 'menu':
        return (
          <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeMiterlimit="10"
            strokeWidth="32"
            d="M80 160h352M80 256h352M80 352h352"
          />
        );
      case 'microphone':
        return (
          <path
            fill="currentColor"
            d="m272.166 382.949v33.106h64 0c8.837 0 16 7.163 16 16 0 8.837-7.163 16-16 16h-160 0c-8.837 0-16-7.163-16-16 0-8.837 7.163-16 16-16h64v-33.106h0c-63.974-8.035-111.974-62.418-112-126.894v-32 0c0-8.837 7.163-16 16-16 8.837 0 16 7.163 16 16v32 0c0 53.019 42.981 96 96 96 53.019 0 96-42.981 96-96v-32 0c0-8.837 7.163-16 16-16 8.837 0 16 7.163 16 16v32l0-.008c-.022 64.477-48.018 118.862-111.992 126.902zm-16-62.894h0c-35.346 0-64-28.654-64-64v-128 0c0-35.346 28.654-64 64-64 35.346 0 64 28.654 64 64v128 0c0 35.346-28.654 64-64 64 0 0 0 0 0 0z"
          />
        );
      case 'microphoneOff':
        return (
          <path
            fill="currentColor"
            d="m311.333 266.306 0 0c1.854 1.864 4.868 1.872 6.732.018 .739-.735 1.217-1.693 1.359-2.726l0 0c.312-2.503.47-5.022.471-7.544v-128l0 0c.125-35.328-28.413-64.068-63.74-64.193-35.328-.125-64.068 28.413-64.193 63.74-.001.151-.001.302 0 .452v12.282 0c0 4.245 1.686 8.316 4.688 11.318zm116.122 138.378-320.091-320.281 0 0c-6.255-6.251-16.392-6.247-22.642.007-6.251 6.255-6.247 16.392.007 22.642l320.095 320.281 0 0c6.255 6.249 16.391 6.245 22.64-.009 6.249-6.255 6.245-16.391-.009-22.64zm-164.273-85.068h0c2.623-.294 4.511-2.659 4.216-5.282-.12-1.072-.599-2.071-1.36-2.836l-65.967-65.948 0 0c-1.856-1.855-4.865-1.854-6.72.002-.89.891-1.391 2.099-1.391 3.358v7.141l0 .017c0 35.337 28.646 63.983 63.983 63.983h.001 0c2.419-.001 4.836-.146 7.238-.434zm99.157-2.32 0 0c1.548 1.565 4.071 1.579 5.635.031 .267-.265.496-.566.679-.894l0 0c9.999-18.554 15.225-39.303 15.208-60.38v-32l0 0c.062-8.832-7.047-16.043-15.879-16.105-8.832-.062-16.043 7.047-16.105 15.879-.001.075-.001.151 0 .226v32l0 .001c-.006 12.916-2.64 25.696-7.741 37.562l0 0c-1.007 2.272-.524 4.93 1.219 6.702zm-26.454 98.758h-63.966v-33.105l0 0c15.487-1.96 30.479-6.766 44.218-14.177l0 0c1.926-1.071 2.62-3.501 1.548-5.428-.184-.33-.413-.633-.682-.899l-16.898-16.892 0 0c-1.771-1.741-4.424-2.224-6.695-1.221l0 0c-16.185 6.955-33.984 9.277-51.411 6.706l0 0c-47.586-7.626-82.45-48.892-82.023-97.083v-29.902c0-8.832-7.16-15.991-15.991-15.991-8.832 0-15.991 7.16-15.991 15.991v32l0-.023c0 64.461 47.963 118.851 111.918 126.915v33.105h-63.943 0c-8.837 0-16 7.163-16 16 0 8.837 7.163 16 16 16h159.92 0c8.837 0 16-7.163 16-16 0-8.837-7.163-16-16-16z"
          />
        );
      case 'navSelectedItemCurve':
        return (
          <path
            fill="currentColor"
            d="M511.983 3.127v504.601a244.547 244.547 0 00-71.606-169.775c-20.525-20.526-31.807-46.795-33.846-73.635v-17.79c2.039-26.84 13.321-53.11 33.846-73.635A244.542 244.542 0 00511.983 3.127z"
          />
        );
      case 'open':
        return (
          <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="48"
            d="M384 224v184a40 40 0 0 1-40 40H104a40 40 0 0 1-40-40V168a40 40 0 0 1 40-40h167.48M336 64h112v112M224 288L440 72"
          />
        );
      case 'pause':
        return (
          <path
            fill="currentColor"
            d="M208 432h-48a16 16 0 01-16-16V96a16 16 0 0116-16h48a16 16 0 0116 16v320a16 16 0 01-16 16zm144 0h-48a16 16 0 01-16-16V96a16 16 0 0116-16h48a16 16 0 0116 16v320a16 16 0 01-16 16z"
          />
        );
      case 'peopleCircle':
        return (
          <>
            <path
              fill="currentColor"
              d="M256 464c-114.69 0-208-93.31-208-208S141.31 48 256 48s208 93.31 208 208-93.31 208-208 208zm0-384c-97 0-176 79-176 176s79 176 176 176 176-78.95 176-176S353.05 80 256 80z"
            />
            <path
              fill="currentColor"
              d="M323.67 292c-17.4 0-34.21-7.72-47.34-21.73a83.76 83.76 0 01-22-51.32c-1.47-20.7 4.88-39.75 17.88-53.62S303.38 144 323.67 144c20.14 0 38.37 7.62 51.33 21.46s19.47 33 18 53.51a84 84 0 01-22 51.3C357.86 284.28 341.06 292 323.67 292zm55.81-74zm-215.66 77.36c-29.76 0-55.93-27.51-58.33-61.33-1.23-17.32 4.15-33.33 15.17-45.08s26.22-18 43.15-18 32.12 6.44 43.07 18.14 16.5 27.82 15.25 45c-2.44 33.77-28.6 61.27-58.31 61.27zm256.55 59.92c-1.59-4.7-5.46-9.71-13.22-14.46-23.46-14.33-52.32-21.91-83.48-21.91-30.57 0-60.23 7.9-83.53 22.25-26.25 16.17-43.89 39.75-51 68.18-1.68 6.69-4.13 19.14-1.51 26.11a192.18 192.18 0 00232.75-80.17zm-256.74 46.09c7.07-28.21 22.12-51.73 45.47-70.75a8 8 0 00-2.59-13.77c-12-3.83-25.7-5.88-42.69-5.88-23.82 0-49.11 6.45-68.14 18.17-5.4 3.33-10.7 4.61-14.78 5.75a192.84 192.84 0 0077.78 86.64l1.79-.14a102.82 102.82 0 013.16-20.02z"
            />
          </>
        );
      case 'personCircle':
        return (
          <>
            <path
              fill="currentColor"
              d="M258.9 48C141.92 46.42 46.42 141.92 48 258.9c1.56 112.19 92.91 203.54 205.1 205.1 117 1.6 212.48-93.9 210.88-210.88C462.44 140.91 371.09 49.56 258.9 48zm126.42 327.25a4 4 0 01-6.14-.32 124.27 124.27 0 00-32.35-29.59C321.37 329 289.11 320 256 320s-65.37 9-90.83 25.34a124.24 124.24 0 00-32.35 29.58 4 4 0 01-6.14.32A175.32 175.32 0 0180 259c-1.63-97.31 78.22-178.76 175.57-179S432 158.81 432 256a175.32 175.32 0 01-46.68 119.25z"
            />
            <path
              fill="currentColor"
              d="M256 144c-19.72 0-37.55 7.39-50.22 20.82s-19 32-17.57 51.93C191.11 256 221.52 288 256 288s64.83-32 67.79-71.24c1.48-19.74-4.8-38.14-17.68-51.82C293.39 151.44 275.59 144 256 144z"
            />
          </>
        );
      case 'play':
        return (
          <path
            fill="currentColor"
            d="M133 440a35.37 35.37 0 01-17.5-4.67c-12-6.8-19.46-20-19.46-34.33V111c0-14.37 7.46-27.53 19.46-34.33a35.13 35.13 0 0135.77.45l247.85 148.36a36 36 0 010 61l-247.89 148.4A35.5 35.5 0 01133 440z"
          />
        );
      case 'premierePro':
        return (
          <path
            fill="currentColor"
            d="M421.333 0C471.467 0 512 41.573 512 92.991V419.01C512 470.427 471.467 512 421.333 512H90.667C40.533 512 0 470.427 0 419.009V92.99C0 41.573 40.533 0 90.667 0h330.666zm0 22H90.667c-37.478 0-68.05 30.971-68.658 69.812L22 92.992v326.017c0 38.997 30.197 70.358 67.533 70.982l1.134.009h330.666c37.478 0 68.05-30.971 68.658-69.812l.009-1.18V92.992c0-38.997-30.197-70.358-67.533-70.982L421.333 22zm-5.76 163.108v43.542c0 1.75-1.066 2.406-3.413 2.406-7.68-.218-15.573.438-23.04 2.188a86.23 86.23 0 00-17.92 5.908c-4.053 1.97-7.893 4.595-10.88 8.096v111.59c0 2.188-.853 3.063-2.773 3.063H315.52c-1.707.219-3.2-.875-3.413-2.626V237.183c0-5.251 0-10.721-.214-16.41-.213-5.69-.213-11.378-.426-17.067 0-5.032-.427-9.846-.854-14.879-.213-1.094.427-2.188 1.494-2.406 0-.22.426-.22.64 0h37.333c2.133 0 3.84 1.531 4.48 3.5.64 1.75 1.067 3.501 1.28 5.47.427 2.188.853 4.595 1.067 6.783.213 2.407.426 5.033.426 7.877a79.8 79.8 0 0122.827-18.817c9.813-5.689 21.12-8.533 32.427-8.533 1.493-.219 2.773.875 2.986 2.407zm-225.706-54.92c17.493 0 32 2.188 43.946 6.783a75.88 75.88 0 0128.587 17.942c6.827 7.002 12.16 15.535 15.573 24.943 3.2 9.19 4.907 18.599 4.907 28.445 0 18.817-4.267 34.352-12.8 46.605-8.533 12.253-20.48 21.443-34.347 26.694-14.506 5.47-30.506 7.44-48 7.44h-1.485c-4.305-.003-7.261-.023-9.181-.22-1.948-.2-4.607-.217-8.14-.218h-1.034v70.236c.214 1.531-.853 2.844-2.346 3.063H124.16c-1.707 0-2.56-.875-2.56-2.845V133.908c0-1.532.64-2.407 2.133-2.407h1.476c3.11-.004 6.233-.03 10.471-.219 5.12-.219 10.453-.219 16.213-.438 5.76-.218 11.947-.218 18.56-.437 6.614-.219 13.014-.219 19.414-.219zm1.493 43.104h-1.73c-4.84.003-9.15.023-12.777.22-4.266-.22-7.253 0-8.746.218v73.518c2.986.219 5.76.437 8.32.437h11.306c8.32 0 16.64-1.312 24.534-3.938 6.826-1.97 12.8-6.126 17.493-11.597 4.48-5.47 6.613-12.909 6.613-22.536a36.734 36.734 0 00-4.906-19.474c-3.627-5.689-8.747-10.065-14.934-12.472-7.893-3.282-16.426-4.594-25.173-4.376z"
          />
        );
      case 'roam':
        return <circle fill="currentColor" cx="50%" cy="50%" r="50%" />;
      case 'remove':
        return (
          <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="48"
            d="M400 256H112"
          />
        );
      case 'search':
        return (
          <path
            fill="currentColor"
            d="M456.69 421.39L362.6 327.3a173.81 173.81 0 0034.84-104.58C397.44 126.38 319.06 48 222.72 48S48 126.38 48 222.72s78.38 174.72 174.72 174.72A173.81 173.81 0 00327.3 362.6l94.09 94.09a25 25 0 0035.3-35.3zM97.92 222.72a124.8 124.8 0 11124.8 124.8 124.95 124.95 0 01-124.8-124.8z"
          />
        );
      case 'share':
        return (
          <path
            d="M336 192h40a40 40 0 0140 40v192a40 40 0 01-40 40H136a40 40 0 01-40-40V232a40 40 0 0140-40h40m160-64l-80-80-80 80m80 193V48"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="32"
          />
        );
      case 'videoCamera':
        return (
          <path
            fill="currentColor"
            d="m454.209 366.948c-5.98 2.478-12.864 1.109-17.44-3.469l-80-80 0 0c-3.001-3-4.687-7.069-4.688-11.313v-32l0 .003c0-4.243 1.686-8.313 4.686-11.314l80-80 0 0c6.249-6.247 16.38-6.246 22.627.003 2.999 3 4.684 7.068 4.685 11.309v192 .001c0 6.469-3.895 12.301-9.871 14.779zm-358.129-238.782h192 0c26.51 0 48 21.49 48 48v160c0 26.51-21.49 48-48 48h-192 0c-26.51 0-48-21.49-48-48v-160 0c0-26.51 21.49-48 48-48z"
          />
        );
      case 'videoCameraOff':
        return (
          <path
            fill="currentColor"
            d="m48.063 175.98 0 0c.009-4.267 3.476-7.718 7.742-7.709 2.043.004 4.001.818 5.446 2.262l200.469 200.411c2.986 2.987 2.986 7.83-.002 10.816-1.434 1.434-3.379 2.239-5.406 2.239h-160.236 0c-26.513.002-48.008-21.487-48.013-48zm406.234-30.785 0 0c5.979 2.477 9.877 8.311 9.877 14.783v192.022l0 .001c.003 8.837-7.158 16.002-15.994 16.006-4.249.001-8.324-1.687-11.326-4.693l-80.019-80.009 0 0c-3.002-3-4.689-7.07-4.69-11.314v-32l0 0c.001-4.244 1.689-8.314 4.691-11.314l80.02-80.012 0 0c4.577-4.576 11.46-5.945 17.44-3.469zm-166.168-17.22h0c26.514-.002 48.01 21.49 48.012 48.004 0 0 0 .001 0 .001v160.02.374l0 0c-.11 4.27-3.661 7.643-7.931 7.532-1.991-.051-3.886-.869-5.29-2.283l-200.64-200.59 0 0c-2.987-2.985-2.989-7.826-.004-10.814 1.435-1.436 3.382-2.242 5.412-2.242zm-244.811-43.309 320.143 320.099 0 0c6.25 6.252 6.248 16.387-.004 22.636-6.252 6.25-16.387 6.248-22.636-.004 0 0 0 0 0 0l-320.14-320.099 0 0c-6.25-6.252-6.248-16.387.004-22.636 6.252-6.25 16.387-6.248 22.636.004z"
          />
        );
      case 'volumeHigh':
        return (
          <path
            d="M126 192H56a8 8 0 00-8 8v112a8 8 0 008 8h69.65a15.93 15.93 0 0110.14 3.54l91.47 74.89A8 8 0 00240 392V120a8 8 0 00-12.74-6.43l-91.47 74.89A15 15 0 01126 192zm194 128c9.74-19.38 16-40.84 16-64 0-23.48-6-44.42-16-64m48 176c19.48-33.92 32-64.06 32-112s-12-77.74-32-112m48 272c30-46 48-91.43 48-160s-18-113-48-160"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="32"
          />
        );
      case 'volumeLow':
        return (
          <path
            d="M126 192H56a8 8 0 00-8 8v112a8 8 0 008 8h69.65a15.93 15.93 0 0110.14 3.54l91.47 74.89A8 8 0 00240 392V120a8 8 0 00-12.74-6.43l-91.47 74.89A15 15 0 01126 192zm194 128c9.74-19.38 16-40.84 16-64 0-23.48-6-44.42-16-64"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="32"
          />
        );
      case 'volumeMedium':
        return (
          <path
            d="M126 192H56a8 8 0 00-8 8v112a8 8 0 008 8h69.65a15.93 15.93 0 0110.14 3.54l91.47 74.89A8 8 0 00240 392V120a8 8 0 00-12.74-6.43l-91.47 74.89A15 15 0 01126 192zm194 128c9.74-19.38 16-40.84 16-64 0-23.48-6-44.42-16-64m48 176c19.48-33.92 32-64.06 32-112s-12-77.74-32-112"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="32"
          />
        );
      case 'volumeOff':
        return (
          <path
            d="M126 192H56a8 8 0 00-8 8v112a8 8 0 008 8h69.65a15.93 15.93 0 0110.14 3.54l91.47 74.89A8 8 0 00240 392V120a8 8 0 00-12.74-6.43l-91.47 74.89A15 15 0 01126 192z"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="32"
          />
        );
      default:
        const _exhaustiveCheck: never = name;
        throw new Error(`Unexpected icon: ${_exhaustiveCheck}`);
    }
  })();

  return (
    <div className={styles.icon} onClick={onClick}>
      <svg
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
        width={`${viewBoxWidth / defaultIconSize}em`}
        height={`${viewBoxHeight / defaultIconSize}em`}
        className={cx({ alignBaseline: alignBaseline !== false })}
      >
        {content}
      </svg>
    </div>
  );
};

export default Icon;
